import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Package, Users, Swords, Newspaper, ShoppingBag, LayoutGrid, Share2, Palette, Type, HelpCircle, Scale, KeyRound, Images, Menu, ShieldCheck, ShieldAlert, Gauge, TicketPercent, Mail, Handshake, Activity, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import OverviewTab from "@/pages/admin/OverviewTab";
import OrdersTab from "@/pages/admin/OrdersTab";
import PromoTab from "@/pages/admin/PromoTab";
import ProductsTab from "@/pages/admin/ProductsTab";
import RostersTab from "@/pages/admin/RostersTab";
import MatchesTab from "@/pages/admin/MatchesTab";
import NewsTab from "@/pages/admin/NewsTab";
import NewsletterTab from "@/pages/admin/NewsletterTab";
import PartnersTab from "@/pages/admin/PartnersTab";
import ActivityTab from "@/pages/admin/ActivityTab";
import FanWallTab from "@/pages/admin/FanWallTab";
import PollsTab from "@/pages/admin/PollsTab";
import StatsTab from "@/pages/admin/StatsTab";
import SocialsTab from "@/pages/admin/SocialsTab";
import AppearanceTab from "@/pages/admin/AppearanceTab";
import HomeTextsTab from "@/pages/admin/HomeTextsTab";
import FaqTab from "@/pages/admin/FaqTab";
import LegalTab from "@/pages/admin/LegalTab";
import AccountTab from "@/pages/admin/AccountTab";

const GROUPS = [
  {
    title: "Pilotage",
    items: [
      { value: "overview", label: "Vue d'ensemble", icon: Gauge, testid: "admin-tab-overview" },
    ],
  },
  {
    title: "Boutique & Ventes",
    items: [
      { value: "orders", label: "Commandes", icon: ShoppingBag, testid: "admin-tab-orders" },
      { value: "products", label: "Produits & Stocks", icon: Package, testid: "admin-tab-products" },
      { value: "promos", label: "Codes promo", icon: TicketPercent, testid: "admin-tab-promos" },
    ],
  },
  {
    title: "E-Sport & Communauté",
    items: [
      { value: "rosters", label: "Rosters", icon: Users, testid: "admin-tab-rosters" },
      { value: "matches", label: "Matchs", icon: Swords, testid: "admin-tab-matches" },
      { value: "news", label: "Actualités", icon: Newspaper, testid: "admin-tab-news" },
      { value: "fanwall", label: "Mur de fans", icon: Images, testid: "admin-tab-fanwall" },
      { value: "newsletter", label: "Newsletter", icon: Mail, testid: "admin-tab-newsletter" },
      { value: "partners", label: "Partenaires", icon: Handshake, testid: "admin-tab-partners" },
      { value: "polls", label: "Sondages", icon: BarChart3, testid: "admin-tab-polls" },
    ],
  },
  {
    title: "Gestion du Site",
    items: [
      { value: "hometexts", label: "Accueil", icon: Type, testid: "admin-tab-hometexts" },
      { value: "faq", label: "FAQ", icon: HelpCircle, testid: "admin-tab-faq" },
      { value: "legal", label: "Légal", icon: Scale, testid: "admin-tab-legal" },
    ],
  },
  {
    title: "Configuration & Stats",
    items: [
      { value: "stats", label: "Statistiques", icon: LayoutGrid, testid: "admin-tab-stats" },
      { value: "socials", label: "Réseaux & Contact", icon: Share2, testid: "admin-tab-socials" },
      { value: "appearance", label: "Apparence", icon: Palette, testid: "admin-tab-appearance" },
      { value: "account", label: "Compte", icon: KeyRound, testid: "admin-tab-account" },
      { value: "activity", label: "Journal d'activité", icon: Activity, testid: "admin-tab-activity" },
    ],
  },
];

const ALL_TABS = GROUPS.flatMap((g) => g.items);

function NavGroups({ onNavigate, badges = {} }) {
  return (
    <div className="grid gap-5">
      {GROUPS.map((g) => (
        <div key={g.title}>
          <p className="mb-2 px-2 text-[11px] font-bold uppercase tracking-[0.2em] text-orange-500/90">{g.title}</p>
          <div className="grid gap-1">
            {g.items.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                data-testid={t.testid}
                onClick={onNavigate}
                className="flex w-full items-center justify-start gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-400 transition-all duration-200 hover:bg-white/5 hover:text-white data-[state=active]:border-l-4 data-[state=active]:border-amber-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-500 data-[state=active]:font-bold data-[state=active]:text-[#060B18] data-[state=active]:shadow-md data-[state=active]:shadow-orange-500/20"
              >
                <t.icon className="h-4 w-4 shrink-0" /> <span className="flex-1 text-left">{t.label}</span>
                {badges[t.value] > 0 && (
                  <span data-testid={`admin-badge-${t.value}`}
                    className="flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 font-mono2 text-[11px] font-bold text-[#060B18]">
                    {badges[t.value]}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [badges, setBadges] = useState({});

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/overview")
        .then((r) => setBadges({ orders: r.data.orders_to_ship, fanwall: r.data.fan_photos_pending }))
        .catch(() => {});
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (user === null) {
    return <div className="py-24 text-center text-slate-400">Chargement...</div>;
  }
  if (user && user.role !== "admin") {
    return (
      <div data-testid="admin-access-denied" className="hero-grid flex min-h-[70vh] items-center justify-center px-4 py-16">
        <div className="card-tactical w-full max-w-md p-8 text-center">
          <ShieldAlert className="mx-auto h-12 w-12 text-orange-400" />
          <h1 className="mt-4 text-2xl font-extrabold uppercase tracking-tight">Accès réservé</h1>
          <p className="mt-2 text-sm text-slate-400">
            Vous êtes connecté avec le compte <span className="font-semibold text-white">{user.email}</span> (compte membre).
            Ce compte n'a pas accès à l'espace d'administration.
          </p>
          <div className="mt-6 grid gap-3">
            <button onClick={() => navigate("/admin/login")} data-testid="admin-denied-login-btn"
              className="rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
              Connexion admin
            </button>
            <button onClick={handleLogout} data-testid="admin-denied-logout-btn"
              className="rounded-xl border border-slate-600 px-6 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:bg-slate-800">
              Se déconnecter de ce compte
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  const activeLabel = ALL_TABS.find((t) => t.value === tab)?.label || "";

  return (
    <Tabs value={tab} onValueChange={setTab} className="w-full">
      <div data-testid="admin-dashboard" className="min-h-screen bg-[#060B18]">
        {/* Sidebar desktop */}
        <aside className="fixed bottom-0 left-0 top-0 z-40 hidden w-64 flex-col border-r border-orange-500/20 bg-[#0B132B]/95 p-4 backdrop-blur-xl md:flex">
          <div className="mb-6 flex items-center gap-2 border-b border-orange-500/10 px-2 pb-4">
            <ShieldCheck className="h-5 w-5 text-orange-500" />
            <span className="font-heading text-lg font-black uppercase tracking-tight">Console Admin</span>
          </div>
          <TabsList className="flex h-auto flex-1 flex-col items-stretch justify-start gap-0 overflow-y-auto bg-transparent p-0">
            <NavGroups badges={badges} />
          </TabsList>
        </aside>

        {/* Barre mobile */}
        <div className="sticky top-0 z-50 flex items-center justify-between border-b border-orange-500/20 bg-[#060B18]/90 px-4 py-3 backdrop-blur-md md:hidden">
          <span className="flex items-center gap-2 font-heading text-base font-black uppercase">
            <ShieldCheck className="h-5 w-5 text-orange-500" /> Admin — {activeLabel}
          </span>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button data-testid="admin-mobile-menu-button" aria-label="Menu admin"
                className="rounded-lg border border-orange-500/40 p-2 text-orange-400">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 overflow-y-auto border-orange-500/20 bg-[#0B132B]">
              <TabsList className="mt-6 flex h-auto flex-col items-stretch justify-start gap-0 bg-transparent p-0">
                <NavGroups badges={badges} onNavigate={() => setSheetOpen(false)} />
              </TabsList>
            </SheetContent>
          </Sheet>
        </div>

        {/* Contenu */}
        <div className="flex min-h-screen flex-1 flex-col md:pl-64">
          <header className="sticky top-0 z-30 hidden items-center justify-between gap-4 border-b border-orange-500/15 bg-[#060B18]/80 px-6 py-4 backdrop-blur-md md:flex">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Administration</p>
              <h1 className="text-xl font-extrabold uppercase tracking-tight text-white sm:text-2xl" data-testid="admin-active-section">{activeLabel}</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="hidden rounded-lg border border-orange-500/20 bg-[#0B132B] px-3 py-1.5 text-xs text-slate-400 lg:block" data-testid="admin-user-pill">
                {user.email} — <span className="font-bold uppercase text-orange-400">admin</span>
              </span>
              <button onClick={handleLogout} data-testid="admin-logout-button"
                className="flex items-center gap-2 rounded-lg border border-red-500/40 px-4 py-2 text-sm font-bold uppercase tracking-wider text-red-400 transition-all hover:scale-[1.02] hover:bg-red-500/10">
                <LogOut className="h-4 w-4" /> Déconnexion
              </button>
            </div>
          </header>

          <main className="mx-auto w-full max-w-7xl flex-1 p-4 sm:p-6 lg:p-8">
            {/* Déconnexion visible sur mobile */}
            <div className="mb-6 flex justify-end md:hidden">
              <button onClick={handleLogout} data-testid="admin-logout-button-mobile"
                className="flex items-center gap-2 rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-red-400">
                <LogOut className="h-4 w-4" /> Déconnexion
              </button>
            </div>
            <TabsContent value="overview"><OverviewTab goTo={setTab} /></TabsContent>
            <TabsContent value="orders"><OrdersTab /></TabsContent>
            <TabsContent value="promos"><PromoTab /></TabsContent>
            <TabsContent value="products"><ProductsTab /></TabsContent>
            <TabsContent value="rosters"><RostersTab /></TabsContent>
            <TabsContent value="matches"><MatchesTab /></TabsContent>
            <TabsContent value="news"><NewsTab /></TabsContent>
            <TabsContent value="fanwall"><FanWallTab /></TabsContent>
            <TabsContent value="newsletter"><NewsletterTab /></TabsContent>
            <TabsContent value="polls"><PollsTab /></TabsContent>
            <TabsContent value="partners"><PartnersTab /></TabsContent>
            <TabsContent value="stats"><StatsTab /></TabsContent>
            <TabsContent value="socials"><SocialsTab /></TabsContent>
            <TabsContent value="appearance"><AppearanceTab /></TabsContent>
            <TabsContent value="hometexts"><HomeTextsTab /></TabsContent>
            <TabsContent value="faq"><FaqTab /></TabsContent>
            <TabsContent value="legal"><LegalTab /></TabsContent>
            <TabsContent value="account"><AccountTab /></TabsContent>
            <TabsContent value="activity"><ActivityTab /></TabsContent>
          </main>
        </div>
      </div>
    </Tabs>
  );
}
